import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, of } from "rxjs";
import {
    DtsCountry,
    DtsCreateTaskResponse,
    DtsDerivableInputs,
    DtsTaskFiles,
    DtsTaskParams,
    DtsTaskResponse,
    DtsTaskSummary,
} from "./core/dts.types";

/**
 * The domain types used to live in this file. They now live in
 * `core/dts.types.ts` — framework-free, so the reports v2 Drive to Store
 * section can share them instead of keeping its own drifting copy, and so
 * DTS v2 (built in `geoactivate_front_v3`) can port them verbatim.
 *
 * Re-exported here so every existing `from "./dts.service"` import keeps
 * working untouched.
 */
export * from "./core/dts.types";

@Injectable({ providedIn: "root" })
export class DtsService {
    private readonly baseUrl = "/api/v2/dts/visit-lift";

    constructor(private http: HttpClient) {}

    /**
     * @param extra Datos de plataforma que **no** viajan al modelo DTS y por eso no van en `params`:
     *   el nombre visible de la medición (D-DTS5) y la geoaudiencia elegida a mano (D-DTS3). Ambos
     *   opcionales — omitirlos deja el comportamiento anterior intacto.
     */
    createTask(
        files: DtsTaskFiles,
        params: DtsTaskParams,
        campaignId?: string | number | null,
        zipFilterId?: string | null,
        extra?: { name?: string | null; geoAudienceId?: string | null; notifyOnFinish?: boolean }
    ): Observable<DtsCreateTaskResponse> {
        const form = new FormData();

        if (files.geoaudiencia) {
            form.append("geoaudiencia", files.geoaudiencia);
        }
        form.append("tiendas", files.tiendas);
        if (files.mediasmart) {
            form.append("mediasmart", files.mediasmart);
        }
        if (files.visitas) {
            form.append("visitas", files.visitas);
        }
        if (files.visitas_previas) {
            form.append("visitas_previas", files.visitas_previas);
        }

        Object.entries(params).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") {
                return;
            }
            if (Array.isArray(value)) {
                value.forEach((item) => form.append(`params[${key}][]`, String(item)));
                return;
            }
            form.append(`params[${key}]`, String(value));
        });

        if (campaignId !== null && campaignId !== undefined && campaignId !== "") {
            form.append("campaign_id", String(campaignId));
        }
        if (zipFilterId !== null && zipFilterId !== undefined && zipFilterId !== "") {
            form.append("zip_filter_id", zipFilterId);
        }
        if (extra?.name) {
            form.append("name", extra.name);
        }
        if (extra?.geoAudienceId) {
            form.append("geo_audience_id", extra.geoAudienceId);
        }

        // Se manda SIEMPRE, también cuando es false. Laravel lee un `FormData` ausente como "no
        // enviado", no como false, así que omitirlo dejaría al backend con su default; hoy coincide,
        // pero ata el comportamiento del front a un default ajeno que puede cambiar sin avisar.
        form.append("notify_on_finish", extra?.notifyOnFinish ? "1" : "0");

        return this.http.post<DtsCreateTaskResponse>(`${this.baseUrl}/tasks`, form);
    }

    getTask(uuid: string): Observable<DtsTaskResponse> {
        return this.http.get<DtsTaskResponse>(`${this.baseUrl}/tasks/${uuid}`);
    }

    /**
     * Rollout controlado del módulo DTS: el backend decide (allowlist de
     * organizaciones + super-admin) y la SPA solo pinta la pestaña si
     * `enabled` es true. Nunca responde 403 — es el único endpoint DTS
     * fuera del gate.
     */
    getEnabled(): Observable<{ enabled: boolean }> {
        return this.http.get<{ enabled: boolean }>(`${this.baseUrl}/enabled`);
    }

    /**
     * Subcategorias de una vertical, para el desplegable del asistente.
     *
     * Vienen del backend y no de una constante del front: son 251 y las mantiene el equipo del modelo,
     * asi que embeberlas aqui obligaria a desplegar la SPA cada vez que anaden una. Si la llamada
     * falla se responde lista vacia y el asistente sigue: la subcategoria es opcional, sin ella se
     * mide con la vertical.
     */
    getSubcategories(categoria: string, pais: DtsCountry = "ES"): Observable<string[]> {
        // Portugal tiene su propio benchmark y sus propios tipos de establecimiento.
        let params = new HttpParams().set("categoria", categoria);
        if (pais !== "ES") params = params.set("pais", pais);

        return this.http
            .get<string[]>(`${this.baseUrl}/subcategories`, { params })
            .pipe(catchError(() => of([])));
    }

    getDerivableInputs(campaignId: string | number): Observable<DtsDerivableInputs> {
        const params = new HttpParams().set("campaign_id", String(campaignId));
        return this.http.get<DtsDerivableInputs>(`${this.baseUrl}/derivable-inputs`, { params });
    }

    listByCampaign(campaignId: string | number): Observable<DtsTaskSummary[]> {
        const params = new HttpParams().set("campaign_id", String(campaignId));
        return this.http.get<DtsTaskSummary[]>(`${this.baseUrl}/tasks`, { params });
    }

    /**
     * Todas las mediciones visibles para el usuario, de todas sus campañas.
     *
     * Es el mismo endpoint que `listByCampaign` **sin** `campaign_id`, no uno nuevo: la ruta ya era
     * plana y lo único que la ataba a una campaña era que el parámetro fuese obligatorio.
     *
     * Cada fila trae aquí su campaña y su titular ya calculados, así que la lista se pinta sin pedir
     * el detalle de ninguna medición. Dentro de la pestaña ese N+1 salía barato porque eran las de una
     * campaña; sobre toda la organización, no.
     */
    listAll(): Observable<DtsTaskSummary[]> {
        return this.http.get<DtsTaskSummary[]>(`${this.baseUrl}/tasks`);
    }

    deleteTask(uuid: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/tasks/${uuid}`);
    }
}
